/**
 * WizardStepEditions
 *
 * Step 4 of the launchpad creation wizard for edition (print-style) launches.
 * The creator adds artworks, each with a name, quantity (max supply), and an
 * optional per-edition price. Buyers later pick a specific artwork to mint.
 *
 * Upload flow: presign (tokenNumber = editionNumber) → PUT to S3 →
 * POST /launches/:id/register-editions (replace-all).
 *
 * See docs/specs/EDITION_LAUNCHES.md
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../../../ui/components/Button';
import { FeatherPlus, FeatherTrash2, FeatherUpload, FeatherCheck } from '@subframe/core';
import {
  getPresignedAssetUrls,
  uploadToS3,
  registerEditions,
  getLaunchEditions,
  type LaunchEdition,
} from '../../../api/launches';

export interface WizardStepEditionsProps {
  launchId: string;
  /** Launch's standard public mint price in drops — the default for editions
   *  that don't set their own price. Used only to show the effective price. */
  defaultPriceDrops?: number | null;
  onUploadComplete: (result: { editions: number; totalCopies: number }) => void;
  onUploadingChange?: (uploading: boolean) => void;
  onError?: (error: string) => void;
}

interface PendingEdition {
  key: string;
  file: File;
  previewUrl: string;
  name: string;
  description: string;
  maxSupply: number;
  /** Price in XRP as entered; empty string = use launch phase pricing */
  priceXrp: string;
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const MAX_EDITIONS = 200;
const MAX_TOTAL_COPIES = 10000;

/** Suggest an artwork name from the filename, but only when it looks like a real
 *  name (has a letter) — so files like "0.png" or "IMG_0001" don't pre-fill the
 *  field with noise; the placeholder shows instead. */
function suggestNameFromFile(file: File): string {
  const base = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
  return /[a-z]/i.test(base) ? base : '';
}

/** Format a drops amount as an XRP label (e.g. "8 XRP", "Free"). */
function fmtXrp(drops: number | null | undefined): string {
  if (drops == null) return '';
  if (drops === 0) return 'Free';
  return `${(drops / 1_000_000).toLocaleString()} XRP`;
}

export const WizardStepEditions: React.FC<WizardStepEditionsProps> = ({
  launchId,
  defaultPriceDrops,
  onUploadComplete,
  onUploadingChange,
  onError,
}) => {
  const [pending, setPending] = useState<PendingEdition[]>([]);
  const [registered, setRegistered] = useState<LaunchEdition[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const keyCounter = useRef(0);

  // Load previously registered editions (e.g. when navigating back to this step)
  useEffect(() => {
    let cancelled = false;
    getLaunchEditions(launchId)
      .then((editions) => {
        if (!cancelled) setRegistered(editions);
      })
      .catch(() => {
        // No editions yet is fine
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [launchId]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPendingCopies = pending.reduce((sum, p) => sum + (p.maxSupply || 0), 0);

  const handleFilesSelected = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const additions: PendingEdition[] = [];
    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        onError?.(`Unsupported file type for ${file.name}. Use PNG, JPG, GIF, or WebP.`);
        continue;
      }
      keyCounter.current += 1;
      additions.push({
        key: `edition-${keyCounter.current}`,
        file,
        previewUrl: URL.createObjectURL(file),
        name: suggestNameFromFile(file),
        description: '',
        maxSupply: 10,
        priceXrp: '',
      });
    }

    setPending((prev) => {
      const combined = [...prev, ...additions];
      if (combined.length > MAX_EDITIONS) {
        onError?.(`Maximum ${MAX_EDITIONS} editions per launch`);
        return combined.slice(0, MAX_EDITIONS);
      }
      return combined;
    });
  }, [onError]);

  const updatePending = (key: string, updates: Partial<PendingEdition>) => {
    setPending((prev) => prev.map((p) => (p.key === key ? { ...p, ...updates } : p)));
  };

  const removePending = (key: string) => {
    setPending((prev) => {
      const item = prev.find((p) => p.key === key);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  };

  const validate = (): string | null => {
    if (pending.length === 0) return 'Add at least one artwork';
    for (const p of pending) {
      if (!p.name.trim()) return 'Every edition needs a name';
      if (!p.maxSupply || p.maxSupply < 1 || p.maxSupply > 10000) {
        return `"${p.name}": quantity must be between 1 and 10,000`;
      }
      if (p.priceXrp.trim() !== '') {
        const price = Number(p.priceXrp);
        if (!Number.isFinite(price) || price < 0) {
          return `"${p.name}": price must be a non-negative number`;
        }
      }
    }
    if (totalPendingCopies > MAX_TOTAL_COPIES) {
      return `Total copies across all editions cannot exceed ${MAX_TOTAL_COPIES.toLocaleString()} (currently ${totalPendingCopies.toLocaleString()})`;
    }
    return null;
  };

  const handleUpload = async () => {
    const validationError = validate();
    if (validationError) {
      onError?.(validationError);
      return;
    }

    try {
      setUploading(true);
      onError?.('');

      // 1. Presign one asset slot per edition (tokenNumber = editionNumber)
      setUploadProgress('Preparing uploads...');
      const { urls } = await getPresignedAssetUrls(
        launchId,
        pending.map((p, index) => ({
          tokenNumber: index + 1,
          contentType: p.file.type,
        }))
      );

      // 2. Upload artwork files directly to S3
      for (let i = 0; i < pending.length; i++) {
        setUploadProgress(`Uploading artwork ${i + 1} of ${pending.length}...`);
        await uploadToS3(urls[i].uploadUrl, pending[i].file, pending[i].file.type);
      }

      // 3. Register editions (replace-all; expands copies server-side)
      setUploadProgress('Registering editions...');
      const result = await registerEditions(
        launchId,
        pending.map((p, index) => ({
          editionNumber: index + 1,
          name: p.name.trim(),
          description: p.description.trim() || null,
          imageUrl: urls[index].cdnUrl,
          maxSupply: p.maxSupply,
          priceDrops: p.priceXrp.trim() !== '' ? Math.round(Number(p.priceXrp) * 1_000_000) : null,
          sortOrder: index + 1,
        }))
      );

      // Refresh registered list and clear pending
      const editions = await getLaunchEditions(launchId);
      setRegistered(editions);
      pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      setPending([]);

      onUploadComplete({ editions: result.editions, totalCopies: result.total_copies });
    } catch (err) {
      console.error('Failed to upload editions:', err);
      onError?.(err instanceof Error ? err.message : 'Failed to upload editions');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const registeredTotalCopies = registered.reduce((sum, e) => sum + e.max_supply, 0);

  // The default an unpriced edition falls back to (the launch's standard mint price).
  const hasDefaultPrice = defaultPriceDrops != null;
  const defaultPriceLabel = fmtXrp(defaultPriceDrops); // '' if unknown, 'Free' if 0
  const pricePlaceholder = hasDefaultPrice
    ? (defaultPriceDrops === 0 ? 'Free' : defaultPriceLabel.replace(' XRP', ''))
    : 'Price';
  const effectivePrice = (ownPriceDrops: number | null): string =>
    ownPriceDrops != null ? fmtXrp(ownPriceDrops) : (hasDefaultPrice ? defaultPriceLabel : '—');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-heading-3 font-heading-3 text-default-font mb-2">Editions</h2>
        <p className="text-body font-body text-subtext-color">
          Add each artwork and set how many copies can be minted. Buyers pick the artwork
          they want and mint a numbered copy (like a signed print). Give an artwork its own
          price, or leave the price blank to use your launch&rsquo;s standard mint price
          {hasDefaultPrice && defaultPriceDrops! > 0 ? ` (${defaultPriceLabel})` : ''}.
        </p>
      </div>

      {/* Already registered editions */}
      {!loading && registered.length > 0 && pending.length === 0 && (
        <div className="p-4 rounded-lg border border-neutral-border bg-neutral-50">
          <div className="flex items-center gap-2 mb-3">
            <FeatherCheck className="w-4 h-4 text-success-600" />
            <span className="text-body-bold font-body-bold text-default-font">
              {registered.length} edition{registered.length === 1 ? '' : 's'} registered
              ({registeredTotalCopies.toLocaleString()} total copies)
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {registered.map((edition) => (
              <div key={edition.id} className="rounded-lg border border-neutral-border overflow-hidden">
                {edition.original_image_key && (
                  <img
                    src={edition.original_image_key}
                    alt={edition.name}
                    className="w-full aspect-square object-cover"
                  />
                )}
                <div className="p-2">
                  <div className="text-caption-bold font-caption-bold text-default-font truncate">
                    {edition.name}
                  </div>
                  <div className="text-caption font-caption text-subtext-color">
                    {edition.max_supply.toLocaleString()} {edition.max_supply === 1 ? 'copy' : 'copies'}
                    {` · ${effectivePrice(edition.price_drops)}`}
                    {edition.price_drops == null && hasDefaultPrice ? ' (launch price)' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-caption font-caption text-subtext-color mt-3">
            Adding new artworks below will replace the current editions.
          </p>
        </div>
      )}

      {/* Add artworks */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          multiple
          className="hidden"
          onChange={(e) => {
            handleFilesSelected(e.target.files);
            e.target.value = '';
          }}
        />
        <Button
          variant="neutral-secondary"
          icon={<FeatherPlus />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          Add Artwork{pending.length > 0 ? 's' : ''}
        </Button>
      </div>

      {/* Pending editions list */}
      {pending.length > 0 && (
        <div className="space-y-3">
          {pending.map((p, index) => (
            <div
              key={p.key}
              className="flex gap-4 p-4 rounded-lg border border-neutral-border items-start"
            >
              <img
                src={p.previewUrl}
                alt={p.name}
                className="w-20 h-20 rounded object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-caption font-caption text-subtext-color flex-shrink-0">
                    #{index + 1}
                  </span>
                  <input
                    type="text"
                    value={p.name}
                    onChange={(e) => updatePending(p.key, { name: e.target.value })}
                    placeholder="Artwork name"
                    disabled={uploading}
                    className="flex-1 min-w-0 px-2 py-1 text-body font-body text-default-font bg-default-background border border-neutral-border rounded focus:outline-none focus:border-brand-600"
                  />
                </div>
                <textarea
                  value={p.description}
                  onChange={(e) => updatePending(p.key, { description: e.target.value })}
                  placeholder="Description (optional)"
                  rows={1}
                  disabled={uploading}
                  className="w-full px-2 py-1 text-caption font-caption text-default-font bg-default-background border border-neutral-border rounded focus:outline-none focus:border-brand-600 resize-none"
                />
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 text-caption font-caption text-subtext-color">
                    Copies
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={p.maxSupply || ''}
                      onChange={(e) => updatePending(p.key, { maxSupply: parseInt(e.target.value, 10) || 0 })}
                      disabled={uploading}
                      className="w-20 px-2 py-1 text-body font-body text-default-font bg-default-background border border-neutral-border rounded focus:outline-none focus:border-brand-600"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-caption font-caption text-subtext-color">
                    Price (XRP)
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={p.priceXrp}
                      onChange={(e) => updatePending(p.key, { priceXrp: e.target.value })}
                      placeholder={pricePlaceholder}
                      disabled={uploading}
                      className="w-24 px-2 py-1 text-body font-body text-default-font bg-default-background border border-neutral-border rounded focus:outline-none focus:border-brand-600"
                    />
                  </label>
                  {p.priceXrp.trim() === '' && (
                    <span className="text-caption font-caption text-subtext-color">
                      {hasDefaultPrice
                        ? `Uses launch price (${defaultPriceLabel})`
                        : 'Set a price, or the launch price'}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removePending(p.key)}
                disabled={uploading}
                className="text-subtext-color hover:text-error-600 flex-shrink-0"
                aria-label={`Remove ${p.name}`}
              >
                <FeatherTrash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="flex items-center justify-between pt-2">
            <span className="text-caption font-caption text-subtext-color">
              {pending.length} edition{pending.length === 1 ? '' : 's'} ·{' '}
              {totalPendingCopies.toLocaleString()} total copies
            </span>
            <Button
              icon={<FeatherUpload />}
              onClick={handleUpload}
              loading={uploading}
              disabled={uploading || pending.length === 0}
            >
              {uploadProgress || `Upload ${pending.length} Edition${pending.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
